import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodChainLocatorAuditDetail,
  fetchFoodChainLocatorAuditStatus,
  postFoodChainLocatorAuditMarkReviewed,
  postFoodBranchLocatorImportDryRun,
} from "../../../../api/foodApi";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import { formatRelativeFreshness } from "../../../../utils/foodFormatters";
import AdminTableContainer from "../layout/AdminTableContainer";

const AUDIT_STATUS_BADGE_CLASS = {
  CURRENT: "food-chain-audit-badge--current",
  DUE_SOON: "food-chain-audit-badge--due-soon",
  STALE: "food-chain-audit-badge--stale",
  NEEDS_SOURCE_REVIEW: "food-chain-audit-badge--needs-review",
  NEEDS_IMPORT: "food-chain-audit-badge--needs-import",
  IMPORT_FAILED: "food-chain-audit-badge--failed",
  IMPORTED_NEEDS_RECONCILIATION: "food-chain-audit-badge--needs-reconciliation",
  RECONCILED: "food-chain-audit-badge--reconciled",
  DO_NOT_USE: "food-chain-audit-badge--blocked",
  NOT_CONFIGURED: "food-chain-audit-badge--not-configured",
};

function auditBadgeClass(status) {
  return AUDIT_STATUS_BADGE_CLASS[status] || "food-chain-audit-badge--not-configured";
}

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toISOString().replace("T", " ").slice(0, 16);
}

const ChainLocatorAuditPage = ({ onClose }) => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [auditStatus, setAuditStatus] = useState(null);
  const [selectedSourceKey, setSelectedSourceKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [dryRunFilePath, setDryRunFilePath] = useState("");
  const [dryRunResult, setDryRunResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setAuditStatus(
        await fetchFoodChainLocatorAuditStatus({ includeAdminLinks: true })
      );
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load chain locator audit status.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      const payload = await fetchFoodChainLocatorAuditDetail(sourceKey, {
        includeAdminLinks: true,
      });
      setDetail(payload);
      setDryRunFilePath(payload.latestFileName ? `docs/food/examples/${payload.latestFileName}` : "");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load chain audit detail.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadDetail(selectedSourceKey);
  }, [selectedSourceKey, loadDetail]);

  const selectedChain = useMemo(
    () => auditStatus?.chains?.find((chain) => chain.sourceKey === selectedSourceKey) || null,
    [auditStatus, selectedSourceKey]
  );

  const handleMarkReviewed = async () => {
    if (!canManage || !selectedSourceKey || busy) return;
    try {
      setBusy(true);
      await postFoodChainLocatorAuditMarkReviewed(selectedSourceKey, {
        notes: reviewNotes,
        sourceStatus: detail?.sourceStatus || selectedChain?.sourceStatus,
      });
      setReviewNotes("");
      await loadStatus();
      await loadDetail(selectedSourceKey);
    } catch (error) {
      setErrorMessage(error.message || "Unable to mark audit reviewed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDryRun = async () => {
    if (!canManage || !selectedChain || !dryRunFilePath || busy) return;
    try {
      setBusy(true);
      setDryRunResult(null);
      setDryRunResult(
        await postFoodBranchLocatorImportDryRun({
          sourceKey: selectedChain.sourceKey,
          chainBrand: selectedChain.chainBrand,
          filePath: dryRunFilePath,
          fileName: dryRunFilePath.split("/").pop(),
        })
      );
    } catch (error) {
      setErrorMessage(error.message || "Dry run failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-page food-chain-locator-audit-page">
      <header className="admin-page__header">
        <div>
          <h1>Chain Locator Audits</h1>
          <p className="muted">
            Quarterly audit workflow for approved supermarket branch locator files. Review freshness,
            source provenance, and safe re-import steps without automatic imports.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Back
          </button>
        ) : null}
      </header>

      {!canManage ? (
        <p className="muted">You have view-only access. Audit actions require source manage permission.</p>
      ) : null}

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      <section className="panel-card">
        <h2>Overall audit status</h2>
        {loading ? (
          <p className="muted">Loading audit status…</p>
        ) : (
          <div className="food-chain-locator-audit-page__kpi-row">
            <div>
              <span className="muted">Overall</span>
              <div>
                <span className={`food-chain-audit-badge ${auditBadgeClass(auditStatus?.overallStatus)}`}>
                  {auditStatus?.overallStatus || "—"}
                </span>
              </div>
            </div>
            <div>
              <span className="muted">Stale after</span>
              <strong>{auditStatus?.staleAfterDays ?? 90} days</strong>
            </div>
            <div>
              <span className="muted">Current chains</span>
              <strong>{auditStatus?.summary?.currentChains ?? 0}</strong>
            </div>
            <div>
              <span className="muted">Stale chains</span>
              <strong>{auditStatus?.summary?.staleChains ?? 0}</strong>
            </div>
            <div>
              <span className="muted">Due soon</span>
              <strong>{auditStatus?.summary?.dueSoonChains ?? 0}</strong>
            </div>
          </div>
        )}
        {auditStatus?.nextRecommendedAction ? (
          <p className="food-chain-locator-audit-page__next-action">
            Next action: {auditStatus.nextRecommendedAction.label}
          </p>
        ) : null}
      </section>

      <section className="panel-card">
        <h2>Chain audit table</h2>
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Chain</th>
                <th>Source key</th>
                <th>Audit status</th>
                <th>Last import</th>
                <th>Days since import</th>
                <th>Next audit due</th>
                <th>Row count</th>
                <th>Branch count</th>
                <th>Needs review</th>
                <th>Last reconciliation</th>
                <th>Next action</th>
              </tr>
            </thead>
            <tbody>
              {(auditStatus?.chains || []).map((chain) => (
                <tr
                  key={chain.sourceKey}
                  className={selectedSourceKey === chain.sourceKey ? "is-selected" : ""}
                  onClick={() => setSelectedSourceKey(chain.sourceKey)}
                >
                  <td>{chain.chainBrand}</td>
                  <td>{chain.sourceKey}</td>
                  <td>
                    <span className={`food-chain-audit-badge ${auditBadgeClass(chain.auditStatus)}`}>
                      {chain.auditStatusLabel || chain.auditStatus}
                    </span>
                  </td>
                  <td>{formatRelativeFreshness(chain.lastImportAt) || "—"}</td>
                  <td>{chain.daysSinceImport ?? "—"}</td>
                  <td>{formatTimestamp(chain.nextAuditDueAt)}</td>
                  <td>{chain.latestRowCount ?? "—"}</td>
                  <td>{chain.branchCount ?? "—"}</td>
                  <td>{chain.needsReviewCount ?? 0}</td>
                  <td>{formatRelativeFreshness(chain.lastReconciliationAt) || "—"}</td>
                  <td>{chain.nextRecommendedAction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>

      {selectedChain ? (
        <section className="panel-card food-chain-locator-audit-page__detail">
          <h2>{selectedChain.chainBrand} audit detail</h2>
          {detailLoading ? <p className="muted">Loading detail…</p> : null}
          {detail?.warnings?.length ? (
            <ul className="food-chain-locator-audit-page__warnings">
              {detail.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}

          <div className="food-chain-locator-audit-page__detail-grid">
            <div>
              <span className="muted">Latest file</span>
              <div>{detail?.latestFileName || "—"}</div>
            </div>
            <div>
              <span className="muted">File hash</span>
              <div className="mono">{detail?.latestFileHash || "—"}</div>
            </div>
            <div>
              <span className="muted">Source status</span>
              <div>{detail?.sourceStatus || "—"}</div>
            </div>
            <div>
              <span className="muted">Reviewed</span>
              <div>{formatTimestamp(detail?.reviewedAt)}</div>
            </div>
          </div>

          <div className="food-chain-locator-audit-page__links">
            <a href="#/admin/food-chain-branch-imports">Chain Branch Imports</a>
            <a href="#/admin/food-store-reconciliation">Store Reconciliation</a>
            <a
              href={`#/admin/food-store-review-queue?sourceKey=${encodeURIComponent(selectedChain.sourceKey)}`}
            >
              Data Quality Review Queue
            </a>
            <a href="#/admin/food-access-operations">Food Access Operations</a>
          </div>

          <h3>Recent import batches</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Imported</th>
                  <th>File</th>
                  <th>Rows</th>
                  <th>Inserted</th>
                  <th>Updated</th>
                  <th>Failed</th>
                  <th>Needs review</th>
                  <th>Cycle</th>
                </tr>
              </thead>
              <tbody>
                {(detail?.recentImportBatches || []).map((batch) => (
                  <tr key={batch.id}>
                    <td>{formatTimestamp(batch.importedAt)}</td>
                    <td>{batch.fileName || "—"}</td>
                    <td>{batch.rowCount ?? "—"}</td>
                    <td>{batch.insertedCount ?? "—"}</td>
                    <td>{batch.updatedCount ?? "—"}</td>
                    <td>{batch.failedCount ?? "—"}</td>
                    <td>{batch.needsReviewCount ?? "—"}</td>
                    <td>{batch.auditCycleLabel || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>

          {canManage ? (
            <Fragment>
              <h3>Mark audit reviewed</h3>
              <label>
                Notes
                <textarea
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                  rows={3}
                />
              </label>
              <button type="button" className="btn btn--primary" disabled={busy} onClick={handleMarkReviewed}>
                Mark reviewed
              </button>

              <h3>Dry-run re-import</h3>
              <p className="muted">
                Use a reviewed branch file path from the repo. Real imports still require confirmation on
                Chain Branch Imports.
              </p>
              <label>
                File path
                <input
                  type="text"
                  value={dryRunFilePath}
                  onChange={(event) => setDryRunFilePath(event.target.value)}
                />
              </label>
              <button type="button" className="btn btn--secondary" disabled={busy} onClick={handleDryRun}>
                Run dry-run import
              </button>
              {dryRunResult ? (
                <pre className="food-chain-locator-audit-page__dry-run-result">
                  {JSON.stringify(dryRunResult, null, 2)}
                </pre>
              ) : null}
            </Fragment>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default ChainLocatorAuditPage;
